import { DataType, DateTime } from '@mode-switch/express';
import { Names } from '@mode-switch/model';
import {
  EncField,
  EncFilterField,
  EncSelectionCondition,
  Encoding,
  EncodingResult,
  FilterType,
  MarkType,
  SelectionMode,
  VariableType,
} from '@mode-vegas/vegas';
import { ChartTypes } from '@mode/shared/contract-common';
import * as EncodingHelpers from './chart-encoding-helpers';
import * as ChartDate from './helpers/chart-date';
import { SchemaModel } from './schema-model';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace ChartSummaryEncoding {
  // HighCharts and Muze return data differently. HighCharts provides the formatted value
  // while Muze provides the raw value. The mapping function below are used to properly
  // convert the value to what is needed for the filter selection.
  type ValueMappingFunctions = {
    boolean: (value: any) => boolean | null;
    number: (value: any) => number | null;
    string: (value: any) => string | null;
    quarter: (value: any) => number | null;
    weekday: (value: any) => number | null;
    month: (value: any) => number | null;
  };

  const DefaultValueMappings = {
    boolean: (value: any) => (value === null ? null : value === true || value === 'true'),
    number: (value: any) => (value === null ? null : parseFloat(value)),
    string: (value: any) => (value === null ? null : `${value}`),
    quarter: (value: any) => (value === null ? null : ChartDate.QuarterNamesByNumber[value]),
    weekday: (value: any) => (value === null ? null : ChartDate.WeekdayNamesByNumber[value]),
    month: (value: any) => (value === null ? null : ChartDate.MonthNamesByNumber[value]),
  };

  const VegasValueMappings = {
    ...DefaultValueMappings,
    quarter: (value: any) => (value === null ? null : parseInt(value, 10)),
    weekday: (value: any) => (value === null ? null : parseInt(value, 10)),
    month: (value: any) => (value === null ? null : parseInt(value, 10)),
  };

  export function make(model: SchemaModel, encoding: Encoding, target: ChartTypes.MarkTarget): EncodingResult {
    let namesOnX = false;
    let fieldOnSize = false;
    let multiSeries = false;
    const markType = getMarkType(encoding);

    if (encoding.marks.series.length > 1) {
      multiSeries = true;
    }

    if (encoding.x.length > 0 && encoding.x[0].formula === Names.Key) {
      namesOnX = true;
    }

    if (encoding.marks.series.length && encoding.marks.series[0].properties.size.values.length) {
      fieldOnSize = true;
    }

    switch (markType) {
      case MarkType.Bar:
        if (multiSeries) {
          return makeCombo(model, encoding, target);
        } else {
          if (namesOnX) {
            return makeGrouped(model, encoding, target);
          } else {
            return makeStacked(model, encoding, target);
          }
        }
      default:
      case MarkType.Line:
      case MarkType.Area:
      case MarkType.TotalArea:
      case MarkType.Circle:
        return makeStacked(model, encoding, target);
      case MarkType.Pie:
      case MarkType.Donut:
        return makeRadial(model, encoding, target);
    }
  }

  export function makeStacked(model: SchemaModel, encoding: Encoding, target: ChartTypes.MarkTarget): EncodingResult {
    const { series, properties } = encoding.marks;
    const props = series.length ? series[0].properties : properties;

    const xEncField = encoding.x[0];
    const colorEncField = props.color.values[0];

    if (xEncField) {
      if (!target.x) {
        throw new Error('target.x is invalid');
      }
      const { name, value } = target.x;
      encoding = addNamesOrFilter(model, encoding, xEncField, name, value).encoding;
    }

    if (colorEncField) {
      if (encoding.value.length === 1) {
        encoding = EncodingHelpers.moveMeasureValuesToY(encoding).encoding;
      } else {
        if (!target.series.color) {
          throw new Error('target.series.color is invalid');
        }
        const { name, value } = target.series.color;
        encoding = addNamesOrFilter(model, encoding, colorEncField, name, value).encoding;
      }
    }

    return EncodingHelpers.loadEncoding(encoding);
  }

  export function makeCombo(model: SchemaModel, encoding: Encoding, target: ChartTypes.MarkTarget): EncodingResult {
    const xEncField = encoding.x[0];
    const yEncField = encoding.y[0];
    const y2EncField = encoding.y2[0];
    const yField = model.buildField(yEncField.formula, yEncField.variable.type, yEncField.id);
    const y2Field = model.buildField(y2EncField.formula, y2EncField.variable.type, y2EncField.id);

    const removeSeriesFromEncoding = (initial: Encoding, series: any): EncodingResult => {
      const y = target.y || target.y2; // user can only select y1 or y2 to view data for
      if (!yField) {
        throw new Error('yField is invalid');
      }
      if (!y) {
        throw new Error('y is invalid');
      }
      if (!y2Field) {
        throw new Error('y2Field is invalid');
      }
      if (series.field === yField.alias) {
        const fieldName = yField.name;
        if (y.name !== fieldName) {
          initial = EncodingHelpers.toggleDualAxisField(initial, y2Field.alias).encoding;
          initial = EncodingHelpers.removeFieldFromEncoding(initial, 'linePlusBar', yField.alias).encoding;
        }
      } else if (series.field === y2Field.alias) {
        const fieldName = y2Field.name;
        if (y.name !== fieldName) {
          initial = EncodingHelpers.removeFieldFromEncoding(initial, 'linePlusBar', y2Field.alias).encoding;
        }
      }

      return EncodingHelpers.loadEncoding(initial);
    };

    if (xEncField) {
      if (!target.x) {
        throw new Error('target.x is invalid');
      }
      const { name, value } = target.x;
      encoding = addNamesOrFilter(model, encoding, xEncField, name, value).encoding;
    }

    encoding.marks.series.forEach((series) => {
      encoding = removeSeriesFromEncoding(encoding, series).encoding;
    });

    return EncodingHelpers.loadEncoding(encoding);
  }

  export function makeGrouped(model: SchemaModel, encoding: Encoding, target: ChartTypes.MarkTarget): EncodingResult {
    const { series, properties } = encoding.marks;
    const props = series.length ? series[0].properties : properties;

    const columnEncField = encoding.column[0];
    const colorEncField = props.color.values[0];

    if (columnEncField) {
      if (!target.columns) {
        throw new Error('target.columns is invalid');
      }
      const { name, value } = target.columns[0];
      encoding = addNamesOrFilter(model, encoding, columnEncField, name, value).encoding;
    }

    if (colorEncField) {
      if (!target.series.color) {
        throw new Error('target.series.color is invalid');
      }
      const { name, value } = target.series.color;
      encoding = addNamesOrFilter(model, encoding, colorEncField, name, value).encoding;
    }

    return EncodingHelpers.loadEncoding(encoding);
  }

  export function makeRadial(model: SchemaModel, encoding: Encoding, target: ChartTypes.MarkTarget): EncodingResult {
    const { series, properties } = encoding.marks;
    const props = series.length ? series[0].properties : properties;

    const colorEncField = props.color.values[0];

    if (colorEncField) {
      if (!target.series.color) {
        throw new Error('target.series.color is invalid');
      }
      const { name, value } = target.series.color;
      encoding = addNamesOrFilter(model, encoding, colorEncField, name, value).encoding;
    }

    return EncodingHelpers.loadEncoding(encoding);
  }

  export function addNamesOrFilter(
    model: SchemaModel,
    encoding: Encoding,
    encField: EncField,
    name: string,
    value: any
  ): EncodingResult {
    return addFieldOrMeasureNamesFilter(model, encoding, encField, value, DefaultValueMappings);
  }

  export function addVegasFilter(model: SchemaModel, encoding: Encoding, encField: EncField, value: any) {
    return addFieldOrMeasureNamesFilter(model, encoding, encField, value, VegasValueMappings);
  }

  function addFieldOrMeasureNamesFilter(
    model: SchemaModel,
    encoding: Encoding,
    encField: EncField,
    value: any,
    mappingFuncs: ValueMappingFunctions
  ) {
    if (encField.formula !== Names.Key) {
      const selection = makeSelection(model, encoding, encField, [value], mappingFuncs);
      if (!selection) {
        throw new Error('selection is invalid');
      }

      const colorFilter: EncFilterField = {
        formula: encField.formula,
        variable: { type: VariableType.DISCRETE },
        selection,
        filterType: FilterType.Discrete,
      };
      const field = model.compileEncField(colorFilter);
      return EncodingHelpers.addFilterToChartEncoding(encoding, colorFilter, field);
    } else {
      return EncodingHelpers.addNamesFilterToChartEncoding(encoding, model, value);
    }
  }

  function getMarkType(encoding: Encoding): MarkType {
    const markType: MarkType = MarkType.Line;
    if (encoding.marks.series.length > 0) {
      if (!encoding.marks.series[0].properties.type.value) {
        throw new Error('encoding.marks.series[0].properties.type.value is invalid');
      }
      return encoding.marks.series[0].properties.type.value;
    } else {
      if (!encoding.marks.properties.type.value) {
        throw new Error('encoding.marks.properties.type.value is invalid');
      }
      return encoding.marks.properties.type.value;
    }
  }

  function makeSelection(
    model: SchemaModel,
    encoding: Encoding,
    encField: EncField,
    values: any[],
    mappingFuncs: ValueMappingFunctions
  ): EncSelectionCondition | undefined {
    const field = model.compileEncField(encField);

    const makeBooleanValues = () => values.map((value) => mappingFuncs['boolean'](value));
    const makeNumberValues = () => values.map((value) => mappingFuncs['number'](value));
    const makeQuarterValues = () => values.map((value) => mappingFuncs['quarter'](value));
    const makeWeekdayValues = () => values.map((value) => mappingFuncs['weekday'](value));
    const makeMonthValues = () => values.map((value) => mappingFuncs['month'](value));
    const makeStringValues = () => values.map((value) => mappingFuncs['string'](value));
    const makeEncSelection = (selected: any[]) => ({ selected, mode: SelectionMode.INCLUDE });

    if (field.hasDateFunc) {
      let dateValues = [];
      if (field.hasDateTrunc) {
        dateValues = makeNumberValues();
      } else {
        if (field.isDiscrete) {
          switch (field.dateFuncUnit) {
            case DateTime.Unit.Weekday:
              dateValues = makeWeekdayValues();
              break;
            case DateTime.Unit.Month:
              dateValues = makeMonthValues();
              break;
            case DateTime.Unit.Quarter:
              dateValues = makeQuarterValues();
              break;
            default:
              dateValues = makeNumberValues();
              break;
          }
        } else {
          dateValues = makeNumberValues();
        }
      }

      return makeEncSelection(dateValues);
    }

    switch (field.dataType) {
      case DataType.Name.Boolean:
        return makeEncSelection(makeBooleanValues());
      case DataType.Name.String:
        return makeEncSelection(makeStringValues());
      case DataType.Name.DateTime:
      case DataType.Name.Timestamp:
        return makeEncSelection(makeNumberValues());
      case DataType.Name.Integer:
      case DataType.Name.Float:
      case DataType.Name.Decimal:
        return makeEncSelection(makeNumberValues());
    }
  }
}
